import type { ShellInfoPagePort } from "./ShellInfoPagePort";
import { isAutospinAvailable } from "../../autospin/autospinAvailability";
import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { LocalizationService } from "../../localization/LocalizationService";
import type { BetSystem } from "../../bet/BetSystemTypes";
import type { GameInitialSettings } from "../../boot/settings/GameInitialSettingsTypes";
import { resolveOfferMultiplier } from "../../feature-purchase/offerMultiplier";
import type { FeaturePurchaseOfferDefinition } from "../../feature-purchase/FeaturePurchaseTypes";
import { InfoContentFetcher } from "./html/InfoContentFetcher";
import { parseInfoHtml } from "./html/parseInfoHtml";
import type { MoneyFormatter } from "../../money/MoneyFormatter";
import type { Log } from "@slot/presentation";

/**
 * Optional hook game-level composition wires in to inject game-specific
 * tokens (paytable values, RTP, hit frequency, etc.) into every info-page
 * render. Called once per `openPage` after the base tokens (`min` / `max` /
 * `steps` / `version` / `presets`) are computed; returned keys are merged
 * on top of the base set, so producers can override base tokens if needed.
 */
export type BuildExtraInfoTokens = (input: {
  betMinor: string;
  formatter: MoneyFormatter;
  /** The operator settings (feature prices, modes, tiers) the game's tokens may derive from. */
  initialSettings: GameInitialSettings;
}) => Record<string, string>;

/**
 * Optional hook through which a title publishes `data-feature` gates of its own (sections the shell
 * cannot know), from the operator settings. Called per `openPage`. A name the shell also publishes
 * keeps the shell's value (decision 27).
 */
export type BuildExtraFeatureAvailability = (input: { initialSettings: GameInitialSettings }) => Record<string, boolean>;

/**
 * Info-page presenter — HTML pipeline.
 *
 * `openPage()` runs the three-stage chain:
 *   1. `InfoContentFetcher.fetchHtml()` — CDN → bundled template fallback (`bundledTemplateUrl`).
 *      Single locale-agnostic template; translation happens during parse.
 *   2. `parseInfoHtml()` — HTML string → typed AST, with token substitution
 *      and `data-feature` gating against the current snapshot; in dev mode
 *      the gate names no flag publishes are logged once (decision 40).
 *   3. `panel.setHtmlContent()` — the DOM page renders the AST (`renderInfoHtmlToDom`).
 *
 * The page holds the `info_panel` interaction block while open (the original let input
 * through; the block is deliberate).
 *
 * Token resolution at open time (in order):
 *   1. Dynamic tokens from `buildTokens()`:
 *        - `{min}` / `{max}` / `{steps}`           formatted bet display
 *        - `{version}`                             game build version
 *        - `{presets}`                             comma-joined autospin presets
 *        - `{paytable.<sym>.tier<N>}`              (via `buildExtraTokens`)
 *        - `{feature.*.multiplier}`                (via `buildExtraTokens`)
 *   2. i18n fall-through via `LocalizationService.t()` — any `{info.<path>}`
 *      placeholder resolves to the active locale's `game.<locale>.json`
 *      value. Resolved values are themselves re-substituted (depth-capped)
 *      so an i18n string containing `{min}` or `{paytable.*}` expands those
 *      dynamic tokens too.
 *   3. Unknown tokens pass through verbatim — surfacing a visible `{xxx}`
 *      in the UI rather than silently emitting empty strings.
 */
export interface ShellInfoPagePresenterDeps {
  interactionBlockStore: InteractionBlockStore;
  localization: LocalizationService;
  betSystem: BetSystem;
  formatter: MoneyFormatter;
  /** The page renders many settings (limits, presets, RTP…); it takes the object. */
  initialSettings: GameInitialSettings;
  gameVersion: string;
  /** The title's purchase offers: each one's info-page gate is its id (decision 27). */
  featurePurchaseOffers: readonly FeaturePurchaseOfferDefinition[];
  /** `GameChromeDefinition.buildExtraFeatureAvailability`: the title's own gates, under the shell's. */
  buildExtraFeatureAvailability?: BuildExtraFeatureAvailability;
  /** `BootLaunchPayload.devModeEnabled`: the unpublished-gates report is a dev-mode line (decision 40). */
  devModeEnabled: boolean;
  log: Log;
}

export class ShellInfoPagePresenter {
  private open = false;
  private fetcher: InfoContentFetcher | null = null;
  private unpublishedGatesReported = false;

  public constructor(
    private readonly panel: ShellInfoPagePort,
    private readonly deps: ShellInfoPagePresenterDeps,
    /**
     * Optional game-specific tokens producer. Called per `openPage` with
     * the current bet and money formatter; returned tokens are merged on
     * top of the base set (`min` / `max` / `steps` / `version` / `presets`).
     * The game supplies it as `GameChromeDefinition.buildExtraInfoTokens`.
     */
    private readonly buildExtraTokens?: BuildExtraInfoTokens,
    /** URL of the bundled fallback template (`AssetRegistry.resolveUrl(shared, "info/html/template")`). */
    private readonly bundledTemplateUrl: string | null = null,
  ) {
    this.panel.bind({
      onClose: () => this.close(),
      onBackdrop: () => this.close(),
    });
  }

  public start(): void {
    this.open = false;
    this.panel.setOpen(false);
  }

  public openPage(): void {
    this.open = true;
    this.panel.setOpen(true);
    this.deps.interactionBlockStore.add("info_panel");
    this.panel.resetScrollToTop();
    void this.renderHtmlPipeline();
  }

  public close(): void {
    this.open = false;
    this.panel.setOpen(false);
    this.deps.interactionBlockStore.remove("info_panel");
  }

  public isOpen(): boolean {
    return this.open;
  }

  public stop(): void {
    this.open = false;
    this.panel.setOpen(false);
    this.deps.interactionBlockStore.remove("info_panel");
  }

  // -------------------------------------------------------------------------
  // HTML pipeline
  // -------------------------------------------------------------------------

  private async renderHtmlPipeline(): Promise<void> {
    const fetcher = this.getFetcher();
    const html = await fetcher.fetchHtml();
    // Bail if the player closed the panel while the fetch was in flight —
    // applying content to a hidden panel still does a bunch of layout work
    // for nothing.
    if (!this.open) return;
    if (html === null) {
      // Both tiers failed. The page used to render its title over nothing, with no way for the
      // player to tell a broken page from an empty one; this is a regulatory surface, so it says so.
      this.deps.log("[ShellInfoPagePresenter] no info HTML available — showing the unavailable notice");
      this.panel.setHtmlContent({
        title: this.deps.localization.t("info.title", "GAME INFO"),
        children: [{ kind: "paragraph", text: this.deps.localization.t("info.unavailable", "Game information could not be loaded. Close this page and try again in a moment.") }],
      });
      return;
    }

    const tokens = this.buildTokens();
    const featureAvailability = this.buildFeatureAvailability();
    // Fall through to LocalizationService for any `{key}` not in the
    // dynamic tokens map. Lets the template HTML address translated strings
    // (e.g. `{info.about.body}`) the same way it addresses dynamic tokens.
    const resolveToken = (name: string): string | undefined =>
      this.deps.localization.has(name) ? this.deps.localization.t(name) : undefined;
    const doc = parseInfoHtml({
      htmlString: html,
      tokens,
      featureAvailability,
      resolveToken,
      // Dev mode only, so a production parse does no extra query.
      onUnpublishedGates: this.deps.devModeEnabled ? (names) => this.reportUnpublishedGates(names, featureAvailability) : undefined,
    });

    if (!this.open) return;
    this.panel.setHtmlContent(doc);
  }

  /**
   * A gate no flag publishes passes, so a template whose names drift from the published ones (`superBonusBuy`
   * against the offer id `super_bonus_buy`) shows rows the operator closed. One line per boot (the presenter is
   * built once; the template and the settings are fixed for the session), with the published flags beside the
   * unknown names so the drift reads off it.
   */
  private reportUnpublishedGates(names: readonly string[], published: Record<string, boolean>): void {
    if (this.unpublishedGatesReported) return;
    this.unpublishedGatesReported = true;
    this.deps.log(
      `[ShellInfoPagePresenter] unpublished info-page gates (no flag names them, so they always pass): ${names.join(", ")}; ` +
        `the published flags: ${Object.keys(published).sort().join(", ")}`,
    );
  }

  private getFetcher(): InfoContentFetcher {
    if (this.fetcher) return this.fetcher;

    const initial = this.deps.initialSettings;
    this.fetcher = new InfoContentFetcher({
      baseUrl: initial.gameInfoContentBaseUrl ?? "",
      templateName: "template",
      bundledUrl: this.bundledTemplateUrl,
      cacheBustToken: this.deps.gameVersion,
      log: this.deps.log,
    });
    return this.fetcher;
  }

  private buildTokens(): Record<string, string> {
    const steps = this.deps.betSystem.getBetStepsMinor();
    const fmt = this.deps.formatter;
    // With lines (decision 56) the ladder is a line's bet and a spin's bet the step times the lines: the least and the
    // most a spin can cost are the lowest step on the fewest lines and the highest on the most. `steps` stays a line's.
    const lineOptions = this.deps.betSystem.getLineOptions();
    const fewestLines = lineOptions.length > 0 ? Math.min(...lineOptions) : 1;
    const mostLines = lineOptions.length > 0 ? Math.max(...lineOptions) : 1;
    const minMinor = (BigInt(steps[0] ?? "0") * BigInt(fewestLines)).toString();
    const maxMinor = (BigInt(steps[steps.length - 1] ?? "0") * BigInt(mostLines)).toString();
    const betMinor = this.deps.betSystem.getCurrentBetMinor();

    const initial = this.deps.initialSettings;
    const presets = (initial.autospinPresets ?? []).join(", ");

    const baseTokens: Record<string, string> = {
      min: fmt.formatMinor(minMinor),
      max: fmt.formatMinor(maxMinor),
      steps: steps.map((s) => fmt.formatMinor(s)).join(", "),
      version: this.deps.gameVersion,
      presets,
      ...(lineOptions.length > 0 ? { lines: lineOptions.join(", ") } : {}),
    };

    // Game-level composition can inject additional tokens (paytable values,
    // RTP, hit frequency, etc.). Merged on top so producers can override
    // base tokens if necessary.
    const extra = this.buildExtraTokens?.({ betMinor, formatter: fmt, initialSettings: this.deps.initialSettings }) ?? {};
    return { ...baseTokens, ...extra };
  }

  private buildFeatureAvailability(): Record<string, boolean> {
    const initial = this.deps.initialSettings;

    // One gate per purchase offer, named by its id: the purchase panel's own rule, so the page never
    // describes an offer the panel does not sell, and a disabled offer's row (with the
    // `{feature.<id>.multiplier}` token a title emits only for an available offer) is dropped (decision 27).
    const offerFlags: Record<string, boolean> = {};
    for (const offer of this.deps.featurePurchaseOffers) offerFlags[offer.id] = resolveOfferMultiplier(offer, initial) !== null;

    const fastSpin = initial.fastSpinEnabled !== false;
    const superFastSpin = initial.superFastSpinEnabled !== false;

    return {
      // The title's own gates first: a shell flag of the same name wins, so a title adds sections but
      // never reopens one the operator's settings closed (decision 27).
      ...this.deps.buildExtraFeatureAvailability?.({ initialSettings: initial }),
      ...offerFlags,
      // `!== false` semantics: undefined wire field defaults to enabled,
      // matching the runtime presenters (AutoSpinPresenter / SpinInputPresenter)
      // and the operator-config brief. Earlier these defaulted to false,
      // which silently hid the section for fixture-static or omitted-field
      // flows even though the runtime control was available.
      // Autospin also needs the counts: the SAME rule the bar applies, so the
      // section and the toggle can never disagree (review finding 21).
      autospin: isAutospinAvailable(initial),
      holdToSpin: initial.holdToSpinEnabled !== false,
      // Accelerated spin speeds — operator-driven jurisdiction gates.
      // Normal speed is always available and not represented as a flag.
      fastSpin,
      superFastSpin,
      // Wraps the whole GAME SPEED section. When both accelerated presets
      // are disabled, only the (always-on) `normal` row would remain — a
      // one-row "selector" with no actual choice. Drop the section entirely
      // to mirror the runtime UI (the speed-mode toggle button is hidden
      // in that same configuration).
      gameSpeed: fastSpin || superFastSpin,
      // The whole FEATURE PURCHASE section is dropped when NO offer is
      // available — avoids rendering an empty section with just the
      // "Where available" preamble and no list items.
      featurePurchase: Object.values(offerFlags).includes(true),
      // The gamble section: the operator's switch, off when absent — the same read as createRuntime's.
      // The rule's other half (the game has a gamble panel) is whether the game's template authors the section.
      gamble: initial.gambleEnabled ?? false,
    };
  }
}
